import React, { useEffect, useRef, useState, useCallback } from "react";
import { Spin, Tag } from "antd";
import { BodyTag, BodyRegion, TAG_SEVERITY_COLORS } from "../../../types/healthcare/BodyAnalysisTypes";

export const REGION_TO_SVG_IDS: Partial<Record<string, string[]>> = {
	head:                    ["craniofacial_tissue", "craniofacial_skeleton_bone"],
	face:                    ["eye", "oral_cavity", "nasopharynx", "salivary_gland"],
	brain:                   ["brain", "frontal_lobe", "prefrontal_cortex", "temporal_lobe", "cerebellum"],
	neck:                    ["trachea", "thyroid"],
	throat:                  ["trachea", "nasopharynx", "tonsils"],
	thyroid:                 ["thyroid"],
	heart:                   ["heart"],
	lung_left:               ["lung"],
	lung_right:              ["lung"],
	chest_left:              ["lung", "heart"],
	chest_right:             ["lung", "heart"],
	breast_left:             ["breast"],
	breast_right:            ["breast"],
	liver:                   ["liver", "gall_bladder"],
	stomach:                 ["stomach", "cardia"],
	pancreas:                ["pancreas"],
	abdomen_upper:           ["abdomen", "liver", "stomach", "pancreas"],
	abdomen_lower:           ["abdomen", "small_intestine", "colon"],
	intestines:              ["small_intestine", "duodenum"],
	// Backend body-tag schema emits the singular "intestine" (AnalysisSchema.php region enum),
	// while the BodyRegion type uses "intestines". Map both so backend tags aren't dropped.
	intestine:               ["small_intestine", "duodenum"],
	colon:                   ["colon", "rectum", "anus", "appendix"],
	bladder:                 ["urinary_bladder"],
	pelvis:                  ["urinary_bladder"],
	groin_left:              ["urinary_bladder"],
	groin_right:             ["urinary_bladder"],
	spine:                   ["spinal_cord"],
	spine_upper:             ["spinal_cord"],
	spine_lower:             ["spinal_cord"],
	upper_back:              ["spinal_cord"],
	mid_back:                ["spinal_cord"],
	lower_back:              ["spinal_cord", "kidney"],
	neck_back:               ["trachea", "thyroid"],
	head_back:               ["craniofacial_tissue", "craniofacial_skeleton_bone"],
	kidney_left:             ["kidney"],
	kidney_right:            ["kidney"],
	adrenal_left:            ["adrenal"],
	adrenal_right:           ["adrenal"],
	shoulder_left:           ["deltoid"],
	shoulder_right:          ["deltoid"],
	shoulder_back_left:      ["deltoid"],
	shoulder_back_right:     ["deltoid"],
	arm_upper_left:          ["deltoid", "skeletal_muscle"],
	arm_upper_right:         ["deltoid", "skeletal_muscle"],
	arm_lower_left:          ["skeletal_muscle"],
	arm_lower_right:         ["skeletal_muscle"],
	hand_left:               ["skeletal_muscle"],
	hand_right:              ["skeletal_muscle"],
	hip_left:                ["vastus_lateralis"],
	hip_right:               ["vastus_lateralis"],
	glute_left:              ["skeletal_muscle"],
	glute_right:             ["skeletal_muscle"],
	leg_upper_left:          ["vastus_lateralis", "femur"],
	leg_upper_right:         ["vastus_lateralis", "femur"],
	leg_upper_back_left:     ["gastrocnemius"],
	leg_upper_back_right:    ["gastrocnemius"],
	knee_left:               ["femur"],
	knee_right:              ["femur"],
	leg_lower_left:          ["tibialis_anterior_muscle"],
	leg_lower_right:         ["tibialis_anterior_muscle"],
	leg_lower_back_left:     ["gastrocnemius"],
	leg_lower_back_right:    ["gastrocnemius"],
	ankle_left:              ["tibialis_anterior_muscle"],
	ankle_right:             ["tibialis_anterior_muscle"],
	foot_left:               ["tibialis_anterior_muscle"],
	foot_right:              ["tibialis_anterior_muscle"],
	foot_back_left:          ["gastrocnemius"],
	foot_back_right:         ["gastrocnemius"],
};

// Bilateral organ bases derived from the *_left / *_right key pairs already present in
// REGION_TO_SVG_IDS — so the set stays in sync with the map instead of a duplicated list.
const BILATERAL_BASES: Record<string, { left: string; right: string }> = (() => {
	const sides: Record<string, { left?: string; right?: string }> = {};
	for (const key of Object.keys(REGION_TO_SVG_IDS)) {
		const m = key.match(/^(.+)_(left|right)$/);
		if (!m) continue;
		(sides[m[1]] ??= {})[m[2] as "left" | "right"] = key;
	}
	const pairs: Record<string, { left: string; right: string }> = {};
	for (const [base, s] of Object.entries(sides)) {
		if (s.left && s.right) pairs[base] = { left: s.left, right: s.right };
	}
	return pairs;
})();

// Inverse of REGION_TO_SVG_IDS: svg element id → the region strings that resolve onto it.
// Derived from the map (not a duplicated literal) so the two stay in sync — the same
// approach BILATERAL_BASES uses. Powers untagged-region clicks (practitioner tagging).
export const SVG_ID_TO_REGIONS: Record<string, string[]> = (() => {
	const map: Record<string, string[]> = {};
	for (const [region, svgIds] of Object.entries(REGION_TO_SVG_IDS)) {
		for (const svgId of svgIds ?? []) {
			(map[svgId] ??= []).push(region);
		}
	}
	return map;
})();

const SIDE_TOKEN = /(?:^|_)(left|right|l|r)(?:$|_)/;

// Resolve an analysis region string to SVG ids. A generic bilateral organ name
// ("lung", "kidney", "shoulder") highlights BOTH sides; an explicit side
// ("left lung", "kidney_right", "l"/"r" short forms) resolves to that side only.
// Unilateral / midline regions (heart, liver, spine, …) are unaffected.
export function resolveRegionToSvgIds(region: string): string[] {
	// Exact key first — covers side-specific keys (lung_left) and all midline/unilateral keys.
	const exact = REGION_TO_SVG_IDS[region];
	if (exact) return exact;

	const norm = region.toLowerCase().trim().replace(/\s+/g, "_");
	const direct = REGION_TO_SVG_IDS[norm];
	if (direct) return direct;

	const sideMatch = norm.match(SIDE_TOKEN);
	const side = sideMatch
		? sideMatch[1] === "l" ? "left" : sideMatch[1] === "r" ? "right" : sideMatch[1]
		: null;
	const base = side ? norm.replace(SIDE_TOKEN, "_").replace(/^_+|_+$/g, "") : norm;

	const pair = BILATERAL_BASES[base];
	if (pair) {
		if (side === "left") return REGION_TO_SVG_IDS[pair.left] ?? [];
		if (side === "right") return REGION_TO_SVG_IDS[pair.right] ?? [];
		// No explicit side → both sides (deduped; in the current art both map to one shared id).
		return Array.from(new Set([
			...(REGION_TO_SVG_IDS[pair.left] ?? []),
			...(REGION_TO_SVG_IDS[pair.right] ?? []),
		]));
	}

	// Side stripped down to a known midline key, else nothing.
	return REGION_TO_SVG_IDS[base] ?? [];
}

// If `region` names one explicit side of a bilateral organ (e.g. "lung_left",
// "left kidney"), returns the shared base ("lung", "kidney") so both sides can be
// merged into one entry. Returns null for unilateral/midline or unsided regions.
export function getBilateralBase(region: string): string | null {
	const norm = region.toLowerCase().trim().replace(/\s+/g, "_");
	const sideMatch = norm.match(SIDE_TOKEN);
	if (!sideMatch) return null;
	const base = norm.replace(SIDE_TOKEN, "_").replace(/^_+|_+$/g, "");
	return BILATERAL_BASES[base] ? base : null;
}

const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, normal: 2 };

const SEVERITY_ANT_COLOR: Record<string, string> = {
	critical: "error",
	warning: "warning",
	normal: "success",
};

const SEVERITY_LABELS: Record<string, string> = {
	critical: "Critical", warning: "Warning", normal: "Normal",
};

function topSeverityTag(tags: BodyTag[]): BodyTag {
	return [...tags].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])[0];
}

function topSeverityColor(tags: BodyTag[]): string {
	return TAG_SEVERITY_COLORS[topSeverityTag(tags).severity] ?? "#52c41a";
}

export const SVG_ID_LABELS: Record<string, string> = {
	brain: "Brain", frontal_lobe: "Frontal Lobe", prefrontal_cortex: "Prefrontal Cortex",
	temporal_lobe: "Temporal Lobe", cerebellum: "Cerebellum",
	craniofacial_tissue: "Head", craniofacial_skeleton_bone: "Skull",
	eye: "Eye", oral_cavity: "Oral Cavity", nasopharynx: "Nasopharynx",
	salivary_gland: "Salivary Gland", trachea: "Trachea", thyroid: "Thyroid",
	tonsils: "Tonsils",
	heart: "Heart", lung: "Lungs", aorta: "Aorta", circulatory_system: "Circulatory System",
	breast: "Breast",
	liver: "Liver", gall_bladder: "Gallbladder", stomach: "Stomach", cardia: "Cardia",
	pancreas: "Pancreas", esophagus: "Esophagus", duodenum: "Duodenum",
	small_intestine: "Small Intestine", colon: "Colon", rectum: "Rectum",
	anus: "Anus", appendix: "Appendix", abdomen: "Abdomen",
	digestive_system_component: "Digestive System",
	urinary_bladder: "Bladder", kidney: "Kidneys", renal_system: "Renal System",
	adrenal: "Adrenal Glands", prostate: "Prostate", testis: "Testes",
	ovary: "Ovaries", uterus: "Uterus", cervix_uterine: "Cervix", vagina: "Vagina",
	placenta: "Placenta",
	spinal_cord: "Spinal Cord",
	deltoid: "Shoulder / Deltoid", skeletal_muscle: "Skeletal Muscle",
	vastus_lateralis: "Thigh Muscle", gastrocnemius: "Calf", tibialis_anterior_muscle: "Shin",
	femur: "Femur", bone_marrow: "Bone Marrow",
	lymph_node: "Lymph Nodes", skin: "Skin", adipose: "Adipose Tissue",
	smooth_muscle: "Smooth Muscle",
};

function buildSvgIdToRegions(tags: BodyTag[]): Map<string, { tags: BodyTag[]; regions: string[] }> {
	const map = new Map<string, { tags: BodyTag[]; regions: string[] }>();
	for (const tag of tags) {
		const svgIds = resolveRegionToSvgIds(tag.region);
		if (svgIds.length === 0 && import.meta.env.DEV) {
			// Region has no SVG mapping — the tag would be silently dropped. Surface drift
			// (e.g. a new backend region key) in development so the map can be updated.
			console.warn(`[BodyMap] No SVG mapping for region "${tag.region}" — tag not highlighted.`);
		}
		for (const svgId of svgIds) {
			if (!map.has(svgId)) map.set(svgId, { tags: [], regions: [] });
			const entry = map.get(svgId)!;
			entry.tags.push(tag);
			if (!entry.regions.includes(tag.region)) entry.regions.push(tag.region);
		}
	}
	return map;
}

function applySvgHighlight(el: Element, color: string) {
	const shapes = el.querySelectorAll("path, circle, ellipse, polygon, polyline, rect");
	const targets = shapes.length > 0 ? Array.from(shapes) : [el];
	for (const t of targets) {
		const s = (t as SVGElement).style;
		s.fill = color;
		s.fillOpacity = "0.75";
		s.cursor = "pointer";
		s.transition = "fill 0.2s, fill-opacity 0.2s";
	}
	(el as SVGElement).style.cursor = "pointer";
}

function applySvgPulse(el: Element) {
	const shapes = el.querySelectorAll("path, circle, ellipse, polygon, polyline, rect");
	const targets = shapes.length > 0 ? Array.from(shapes) : [el];
	for (const t of targets) {
		(t as SVGElement).classList.add("body-svg-pulse");
	}
}

// Pointer cue for a clickable-but-untagged region (tagged regions already get this
// via applySvgHighlight). No fill change — the region is only interactive, not flagged.
function applyPointerCursor(el: Element) {
	const shapes = el.querySelectorAll("path, circle, ellipse, polygon, polyline, rect");
	const targets = shapes.length > 0 ? Array.from(shapes) : [el];
	for (const t of targets) {
		(t as SVGElement).style.cursor = "pointer";
	}
	(el as SVGElement).style.cursor = "pointer";
}

function prepareSvg(raw: string): string {
	return raw
		.replace(/<\?xml[^?]*\?>/g, "")
		.replace(/<!DOCTYPE[^>]*>/g, "")
		.replace(/\bwidth="[^"]*"/, 'width="100%"')
		.replace(/\bheight="[^"]*"/, 'height="100%"');
}

interface TooltipState {
	svgId: string;
	x: number;
	y: number;
}

export interface HumanBodySvgProps {
	gender?: "male" | "female";
	tags: BodyTag[];
	// When true, every mappable region (a key of SVG_ID_TO_REGIONS) is clickable — not
	// just regions that already carry a tag — so a practitioner can tag an empty region.
	allowUntaggedRegionClick?: boolean;
	onRegionClick: (svgId: string, label: string, tags: BodyTag[], regions: string[]) => void;
}

const PULSE_STYLE = `
@keyframes body-svg-pulse-kf {
	0%   { fill-opacity: 0.75; }
	50%  { fill-opacity: 0.45; }
	100% { fill-opacity: 0.75; }
}
.body-svg-pulse {
	animation: body-svg-pulse-kf 2s ease-in-out infinite;
}
`;

const HumanBodySvg: React.FC<HumanBodySvgProps> = ({ gender = "male", tags, allowUntaggedRegionClick = false, onRegionClick }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [svgContent, setSvgContent] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);

	const svgIdMapRef = useRef<Map<string, { tags: BodyTag[]; regions: string[] }>>(new Map());

	useEffect(() => {
		setLoading(true);
		setSvgContent(null);
		const url = gender === "female"
			? "/assets/body/woman_body.svg"
			: "/assets/body/man_body.svg";

		fetch(url)
			.then((r) => r.text())
			.then((text) => setSvgContent(prepareSvg(text)))
			.catch(() => setSvgContent(null))
			.finally(() => setLoading(false));
	}, [gender]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || !svgContent) return;

		const svgIdMap = buildSvgIdToRegions(tags);
		svgIdMapRef.current = svgIdMap;

		for (const [svgId, data] of svgIdMap.entries()) {
			const el = container.querySelector(`#${svgId}`);
			if (!el) continue;
			const color = topSeverityColor(data.tags);
			applySvgHighlight(el, color);
			const hasCritical = data.tags.some((t) => t.severity === "critical");
			if (hasCritical) applySvgPulse(el);
		}

		// Untagged tagging mode: give every mappable region a pointer cursor so the
		// practitioner can tell it is clickable. Tagged regions already show one.
		if (allowUntaggedRegionClick) {
			for (const svgId of Object.keys(SVG_ID_TO_REGIONS)) {
				if (svgIdMap.has(svgId)) continue;
				const el = container.querySelector(`#${svgId}`);
				if (el) applyPointerCursor(el);
			}
		}

		const handleClick = (e: MouseEvent) => {
			let target = e.target as Element | null;
			while (target && target !== container) {
				const id = target.id;
				if (id) {
					const data = svgIdMap.get(id);
					if (data) {
						const label = SVG_ID_LABELS[id] ?? id.replace(/_/g, " ");
						onRegionClick(id, label, data.tags, data.regions);
						return;
					}
					if (allowUntaggedRegionClick && SVG_ID_TO_REGIONS[id]) {
						const label = SVG_ID_LABELS[id] ?? id.replace(/_/g, " ");
						onRegionClick(id, label, [], SVG_ID_TO_REGIONS[id] ?? []);
						return;
					}
				}
				target = target.parentElement;
			}
		};

		const handleMouseMove = (e: MouseEvent) => {
			const containerEl = containerRef.current;
			if (!containerEl) return;
			const rect = containerEl.getBoundingClientRect();
			let target = e.target as Element | null;
			while (target && target !== containerEl) {
				const id = target.id;
				if (id && svgIdMapRef.current.has(id)) {
					setTooltip({ svgId: id, x: e.clientX - rect.left, y: e.clientY - rect.top });
					return;
				}
				target = target.parentElement;
			}
			setTooltip(null);
		};

		const handleMouseLeave = () => setTooltip(null);

		container.addEventListener("click", handleClick);
		container.addEventListener("mousemove", handleMouseMove);
		container.addEventListener("mouseleave", handleMouseLeave);
		return () => {
			container.removeEventListener("click", handleClick);
			container.removeEventListener("mousemove", handleMouseMove);
			container.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [svgContent, tags, onRegionClick, allowUntaggedRegionClick]);

	const tooltipData = tooltip ? svgIdMapRef.current.get(tooltip.svgId) : null;
	const tooltipTopTag = tooltipData ? topSeverityTag(tooltipData.tags) : null;

	return (
		<div
			ref={containerRef}
			style={{
				width: "100%",
				maxWidth: 220,
				margin: "0 auto",
				aspectRatio: "460 / 720",
				position: "relative",
			}}
		>
			<style>{PULSE_STYLE}</style>
			{loading && (
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
					<Spin />
				</div>
			)}
			{svgContent && (
				<div dangerouslySetInnerHTML={{ __html: svgContent }} style={{ width: "100%", height: "100%" }} />
			)}
			{tooltip && tooltipData && tooltipTopTag && (
				<div
					style={{
						position: "absolute",
						left: tooltip.x + 10,
						top: tooltip.y + 10,
						background: "rgba(255,255,255,0.97)",
						border: `1px solid ${TAG_SEVERITY_COLORS[tooltipTopTag.severity]}`,
						borderRadius: 6,
						padding: "6px 10px",
						pointerEvents: "none",
						zIndex: 100,
						boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
						maxWidth: 180,
					}}
				>
					<div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>
						{tooltipData.tags.length} finding{tooltipData.tags.length !== 1 ? "s" : ""}
					</div>
					<Tag
						color={SEVERITY_ANT_COLOR[tooltipTopTag.severity]}
						style={{ margin: 0, fontSize: 10, lineHeight: "16px", marginBottom: 3 }}
					>
						{SEVERITY_LABELS[tooltipTopTag.severity]}
					</Tag>
					<div style={{ fontSize: 12, fontWeight: 600, color: "#222", marginTop: 3, lineHeight: 1.3 }}>
						{tooltipTopTag.title}
					</div>
				</div>
			)}
		</div>
	);
};

export default HumanBodySvg;

export function getRegionCoords(_region: BodyRegion, _view: "front" | "back"): null { return null; }
